import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router';

import { VideaNotesButton } from '@/modules/videaNotes/components/VideaNotesButton';

import type { VisitsResponse } from '../../../@types/visits';
import { ImageViewerMenu } from '../../../app/appHeader/imageViewerMenu/ImageViewerMenu';
import { externalPmsSourceAtom } from '../../../atoms/externalAtoms';
import annotatorConstants from '../../../modules/annotator/annotator.constants';
import {
  showPastVisitsAtom,
  viewerModeAtom,
} from '../../../modules/annotator/atoms';
import { showSegmentationAtom } from '../../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { SegmentationViewToggle } from '../../../modules/annotator/multiFileViewer/imageToolbar/segmentationViewToggle';
import RerunAiButton from '../../../modules/annotator/multiFileViewer/RerunAiButton';
import { useRotation } from '../../../modules/annotator/multiFileViewer/RotationContext';
import { isMultiPracticeUser } from '../../../modules/login/login-utils';
import { mapKeysToPatients } from '../../../modules/uploader/uploader-utils';
import { usePatientVisits } from '../../../react-query/usePatientVisits';
import { usePreferences } from '../../../react-query/usePreferences';
import routesConstants from '../../../routes/routes.constants';
import { theme } from '../../../theme/theme';
import {
  blueBlack500,
  blueUltramarine030,
  blueUltramarine100,
} from '../../../theme/videa-style-tokens';
import { useIsEmbedded } from '../../../utils/hooks';
import { directorPracticeIdAtom } from '../../../utils/hooks/useDirectorPracticeView';
import {
  formatPatientFirstNameAndLastName,
  sortPatientList,
} from '../../../utils/patient';
import { useIsPatientViewEnabled } from '../../../utils/user';
import { VideaTextButton } from '../../elements/VideaButton/VideaButton';
import { FmxIcon } from '../../elements/VideaIcons/VideaIcons';

import { VideaDevTool } from './videaDevTool/VideaDevTool';
interface ActiveApplicationPathProps {
  activeImage: any;
  currentImageIndex: number;
  currentPatient: any;
  patients: any;
  sidebarIsNotCollapsed: boolean;
  sortedAndFilteredImages: any[];
  user: any;
  onLeaveImage: () => void;
  onLeavePatient: () => void;
}

const Container = styled.div`
  z-index: 10;
  box-sizing: border-box;
  position: absolute;
  left: 0;
  right: 24em;
  width: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${theme.typography.fontFamily};

  &.patient-view-expanded {
    right: 0.5em;

    &.fmx-view {
      right: 24em;

      &.container-expanded {
        right: 3em;
      }
    }
  }

  &.container-expanded {
    right: 3em;
  }

  .application-path-container {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 0.75em;
    width: 100%;
    box-sizing: border-box;
    height: 4.5em;

    .logo-group {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: end;

      .dentrix-videa-logo {
        width: 14em;
        transform: translateY(0.25em);
        opacity: 0.75;
        padding-right: 0.5em;
      }
    }

    @media (max-width: 1400px) {
      font-size: 0.8em;
    }

    @media (max-width: 800px) {
      font-size: 0.675em;
    }

    .application-path-group {
      display: flex;
      align-items: center;
      gap: 0.5em;
      padding: 0.5em 0;
    }
  }
  .MuiButton-root.button {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 100;
    padding: 0 0.25em;
    color: ${blueUltramarine100};
    font-weight: 500;
    justify-content: center;
    text-align: left;

    .fmx-icon {
      width: 2.4em;
      margin-right: 0.5em;
      .icon-shape {
        fill: ${blueBlack500};
      }
    }

    & > span {
      display: inline-block;
      position: relative;
      z-index: 5;
      white-space: nowrap;
    }
    & > svg {
      vertical-align: middle;
      margin-right: 0.125em;
    }

    &:hover,
    &:focus {
      color: ${blueUltramarine030};
      .icon-shape {
        fill: ${blueUltramarine030};
        transition: fill 0.2s ease 0s;
      }
      cursor: pointer;
      transition:
        opacity 0.2ms ease 0s,
        color 0.2s ease 0s;
    }

    &:disabled {
      color: ${theme.palette.secondary.main};
    }
  }
  .activeButton {
    color: ${blueUltramarine030};

    .fmx-icon {
      width: 2.4em;
      margin-right: 0.5em;
      .icon-shape {
        fill: ${blueBlack500};
      }
    }

    &:hover,
    &:focus {
      color: ${blueUltramarine030};
      .icon-shape {
        fill: ${blueUltramarine030};
        transition: fill 0.2s ease 0s;
      }
      cursor: pointer;
      transition:
        color 0.2s ease 0s,
        opacity 0.2ms ease 0s;
    }
  }
  .chevron.MuiSvgIcon-root {
    fill: ${theme.palette.primary.light};
    width: 0.5em;
    height: 0.5em;
    padding: 0 0.125em 0 0;
  }
`;

type PatientPathProps = {
  activeImageId: string;
  onLeaveImage: () => void;
  currentPatientIndex: number;
  firstName: string;
  lastName: string;
  sortedAndFilteredImages: any[];
  user: any;
  patientVisitsData?: VisitsResponse;
  currentPatient?: any;
};

const PatientPath: React.FC<PatientPathProps> = ({
  activeImageId,
  currentPatientIndex,
  firstName,
  lastName,
  onLeaveImage,
  sortedAndFilteredImages,
  user,
  currentPatient,
  patientVisitsData,
}) => {
  return (
    <>
      <VideaTextButton
        data-cy="active-patient-button"
        data-testid="active-patient-button"
        className={` ${!activeImageId ? 'activeButton' : 'button'}`}
        onClick={onLeaveImage}
      >
        <FmxIcon />
        <span>
          {formatPatientFirstNameAndLastName(
            firstName ?? currentPatient.name,
            lastName,
          ) ?? `Patient ${currentPatientIndex + 1}`}
        </span>
      </VideaTextButton>
      {!activeImageId && (
        <VideaDevTool
          activeImage={null}
          sortedAndFilteredImages={sortedAndFilteredImages}
        />
      )}
    </>
  );
};

export const ActiveApplicationPath = ({
  activeImage,
  currentPatient,
  onLeaveImage,
  onLeavePatient,
  patients,
  sidebarIsNotCollapsed,
  sortedAndFilteredImages,
  user,
}: ActiveApplicationPathProps) => {
  const { t } = useLingui();
  const { firstName, lastName, patientId } = currentPatient || {};
  const { objectId: activeImageId } = activeImage || {};
  const patientKeys = Object.keys(patients || {});
  const patientData = mapKeysToPatients(patientKeys, patients);
  const sortedPatientList = patientData.sort(sortPatientList);
  const currentPatientIndex = sortedPatientList.findIndex(
    (patient: any) => patient.patientId === patientId,
  );
  const { currentlyRotatingImages, snackbarState } = useRotation();

  const { data: patientVisitsData } = usePatientVisits({
    patientId,
  });
  const [showPastVisits] = useAtom(showPastVisitsAtom);
  const [directorPracticeId, setDirectorPracticeId] = useAtom(
    directorPracticeIdAtom,
  );
  const isPatientViewEnabled = useIsPatientViewEnabled();
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const preferences = usePreferences();
  const externalPmsSource = useAtomValue(externalPmsSourceAtom);
  const setViewerMode = useSetAtom(viewerModeAtom);
  const isEmbedded = useIsEmbedded();
  const history = useHistory();
  const canViewMultiplePractices = isMultiPracticeUser(user);
  const showRerunAiButton =
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    currentlyRotatingImages?.imageIds.length > 0 && !snackbarState.open;

  const applicationPathClasses = classNames({
    'container-expanded': !sidebarIsNotCollapsed,
    'patient-view-expanded': isPatientViewEnabled && showSegmentation,
    'fmx-view': !activeImage,
  });

  const backToSwitchboard = () => {
    setDirectorPracticeId(null);
    history.push(routesConstants.SWITCHBOARD);
  };

  const isSageDeepLinkUserOnVaultPractice =
    user.vaultPracticeVideaId &&
    canViewMultiplePractices &&
    externalPmsSource === 'sage' &&
    directorPracticeId === user.vaultPracticeVideaId;
  const isTableauDeepLinkDirectorUser =
    canViewMultiplePractices && externalPmsSource === 'insights';

  return (
    <>
      <Container
        className={applicationPathClasses}
        data-testid="active-application-path"
      >
        <div className="application-path-container">
          <div className="application-path-group">
            {!isEmbedded && (
              <VideaTextButton
                data-cy="arrow-back"
                className="button"
                onClick={() => {
                  setViewerMode(annotatorConstants.MODES.VIEWING);
                  isSageDeepLinkUserOnVaultPractice ||
                  isTableauDeepLinkDirectorUser
                    ? backToSwitchboard()
                    : onLeavePatient();
                }}
              >
                <ArrowBackIcon />

                <span>
                  {isSageDeepLinkUserOnVaultPractice ||
                  isTableauDeepLinkDirectorUser
                    ? t`Back to Practice List`
                    : t`All Patients`}
                </span>
              </VideaTextButton>
            )}

            {currentPatient && (
              <PatientPath
                activeImageId={activeImageId}
                currentPatientIndex={currentPatientIndex}
                firstName={firstName}
                lastName={lastName}
                onLeaveImage={onLeaveImage}
                sortedAndFilteredImages={sortedAndFilteredImages}
                user={user}
                currentPatient={currentPatient}
                patientVisitsData={patientVisitsData}
              />
            )}

            {activeImageId && (
              <VideaDevTool
                activeImage={activeImage}
                sortedAndFilteredImages={sortedAndFilteredImages}
              />
            )}
            <ImageViewerMenu />
          </div>
          {isEmbedded && !sidebarIsNotCollapsed && (
            <div className="logo-group">
              <img
                src="/assets/images/ddai/dentrix-videaai.svg"
                alt="Dentrix Videa Logo"
                className="dentrix-videa-logo"
              />
            </div>
          )}
          <div className="vai-flex vai-items-center vai-gap-2">
            {!showPastVisits &&
              isPatientViewEnabled &&
              !preferences?.data?.useSidebarRedesign && (
                <SegmentationViewToggle activeImage={activeImage} />
              )}
            {preferences?.data?.isVideaNotesEnabled && <VideaNotesButton />}
          </div>
        </div>
        {showRerunAiButton && !activeImageId && (
          <RerunAiButton className="vai-ml-auto" />
        )}
      </Container>
    </>
  );
};
